import { useState, useEffect, useCallback } from 'react';
import { useParams, useSearchParams, useNavigate } from 'react-router-dom';
import { testAPI } from '../../services/api';
import toast from 'react-hot-toast';
import { 
  Clock, 
  BookOpen, 
  ChevronLeft, 
  ChevronRight, 
  AlertTriangle,
  Send
} from 'lucide-react';

interface Question {
  id: number;
  subject: string;
  topic: string;
  difficulty: string;
  question: string;
  options: string[];
  correctAnswer: number;
  explanation: string;
  marks: number;
  negativeMarks: number;
}

interface Answer {
  questionId: number;
  selectedOption: number | null;
  timeSpent: number;
  answeredAt: Date;
}

export default function TestPage() {
  const { id } = useParams<{ id: string }>();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const attemptId = searchParams.get('attempt');

  const [questions, setQuestions] = useState<Question[]>([]);
  const [currentQuestionIndex, setCurrentQuestionIndex] = useState(0);
  const [answers, setAnswers] = useState<Answer[]>([]);
  const [timeRemaining, setTimeRemaining] = useState(0);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [questionStartTime, setQuestionStartTime] = useState(Date.now());
  const [showSubmitConfirm, setShowSubmitConfirm] = useState(false);

  useEffect(() => {
    if (id) {
      fetchTestQuestions();
    }
  }, [id]);

  useEffect(() => {
    const timer = setInterval(() => {
      setTimeRemaining(prev => {
        if (prev <= 1) {
          handleAutoSubmit();
          return 0;
        }
        return prev - 1;
      });
    }, 1000);

    return () => clearInterval(timer);
  }, []);

  const fetchTestQuestions = async () => {
    try {
      const response = await testAPI.getTestQuestions(id!);
      if (response.data.success) {
        setQuestions(response.data.data.questions);
        setTimeRemaining(response.data.data.duration * 60); // Convert to seconds
      }
    } catch (error: any) {
      toast.error('Failed to load test questions');
      navigate('/tests');
    } finally {
      setLoading(false);
    }
  };

  const saveAnswer = useCallback(async (questionId: number, selectedOption: number | null) => {
    if (!attemptId) return;

    const timeSpent = Math.floor((Date.now() - questionStartTime) / 1000);
    
    try {
      await testAPI.saveAnswer(id!, {
        attemptId: parseInt(attemptId),
        questionId,
        selectedOption,
        timeSpent
      });

      // Update local answers
      setAnswers(prev => {
        const existing = prev.findIndex(a => a.questionId === questionId);
        const newAnswer = {
          questionId,
          selectedOption,
          timeSpent,
          answeredAt: new Date()
        };

        if (existing >= 0) {
          const updated = [...prev];
          updated[existing] = newAnswer;
          return updated;
        } else {
          return [...prev, newAnswer];
        }
      });
    } catch (error) {
      toast.error('Failed to save answer');
    }
  }, [id, attemptId, questionStartTime]);

  const handleOptionSelect = (optionIndex: number) => {
    const currentQuestion = questions[currentQuestionIndex];
    if (currentQuestion) {
      saveAnswer(currentQuestion.id, optionIndex);
    }
  };

  const navigateToQuestion = (index: number) => {
    if (index >= 0 && index < questions.length) {
      setCurrentQuestionIndex(index);
      setQuestionStartTime(Date.now());
    }
  };

  const handleSubmitTest = async () => {
    if (!attemptId) return;

    setSubmitting(true);
    try {
      const response = await testAPI.submitTest(id!, {
        attemptId: parseInt(attemptId)
      });

      if (response.data.success) {
        toast.success('Test submitted successfully!');
        navigate(`/test-result/${attemptId}`);
      }
    } catch (error: any) {
      toast.error(error.response?.data?.error || 'Failed to submit test');
    } finally {
      setSubmitting(false);
      setShowSubmitConfirm(false);
    }
  };

  const handleAutoSubmit = () => {
    toast('Time up! Submitting test automatically...', { icon: '⏰' });
    handleSubmitTest();
  };

  const formatTime = (seconds: number) => {
    const hours = Math.floor(seconds / 3600);
    const minutes = Math.floor((seconds % 3600) / 60);
    const secs = seconds % 60;
    return `${hours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
  };

  const getQuestionStatus = (questionId: number) => {
    const answer = answers.find(a => a.questionId === questionId);
    if (!answer || answer.selectedOption === null) return 'unanswered';
    return 'answered';
  };

  const getAnsweredCount = () => {
    return answers.filter(a => a.selectedOption !== null).length;
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-4 border-primary-500 border-t-transparent mx-auto mb-4"></div>
          <p className="text-secondary-600">Loading test questions...</p>
        </div>
      </div>
    );
  }

  if (questions.length === 0) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <AlertTriangle className="h-12 w-12 text-warning-500 mx-auto mb-4" />
          <h2 className="text-xl font-semibold text-secondary-900 mb-2">No Questions Available</h2>
          <p className="text-secondary-600 mb-4">This test doesn't have any questions yet.</p>
          <button onClick={() => navigate('/tests')} className="flex items-center px-6 py-3 text-sm font-medium text-white bg-primary-600 border border-primary-600 rounded-lg hover:bg-primary-700 transition-colors">
            Back to Tests
          </button>
        </div>
      </div>
    );
  }

  const currentQuestion = questions[currentQuestionIndex];
  const currentAnswer = answers.find(a => a.questionId === currentQuestion.id);

  return (
    <div className="min-h-screen bg-secondary-50">
      {/* Header */}
      <div className="bg-white shadow-sm border-b border-secondary-200 sticky top-0 z-10">
        <div className="max-w-7xl mx-auto px-6 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-4">
              <h1 className="text-xl font-semibold text-secondary-900">JEE Mock Test</h1>
              <div className="flex items-center space-x-2 text-sm text-secondary-600">
                <BookOpen className="h-4 w-4" />
                <span>Question {currentQuestionIndex + 1} of {questions.length}</span>
              </div>
            </div>
            
            <div className="flex items-center space-x-6">
              <div className="flex items-center space-x-2">
                <Clock className="h-5 w-5 text-danger-500" />
                <span className={`font-mono text-lg font-semibold ${
                  timeRemaining < 300 ? 'text-danger-600' : 'text-secondary-900'
                }`}>
                  {formatTime(timeRemaining)}
                </span>
              </div>
              
              <button
                onClick={() => setShowSubmitConfirm(true)}
                className="flex items-center px-6 py-2 text-sm font-medium text-white bg-primary-600 border border-primary-600 rounded-lg hover:bg-primary-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                disabled={submitting}
              >
                <Send className="h-4 w-4 mr-2" />
                Submit Test
              </button>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto p-6">
        <div className="grid grid-cols-1 lg:grid-cols-4 gap-6">
          {/* Question Panel */}
          <div className="lg:col-span-3">
            <div className="card">
              <div className="card-header">
                <div className="flex items-center justify-between">
                  <div className="flex items-center space-x-3">
                    <span className="badge badge-primary">
                      {currentQuestion.subject.charAt(0).toUpperCase() + currentQuestion.subject.slice(1)}
                    </span>
                    <span className="badge badge-secondary">
                      {currentQuestion.topic}
                    </span>
                    <span className="text-sm text-secondary-600">
                      +{currentQuestion.marks} marks, {currentQuestion.negativeMarks} for wrong answer
                    </span>
                  </div>
                </div>
              </div>
              
              <div className="card-content">
                <div className="mb-6">
                  <h2 className="text-lg font-medium text-secondary-900 leading-relaxed">
                    {currentQuestion.question}
                  </h2>
                </div>

                <div className="space-y-3">
                  {currentQuestion.options.map((option, index) => (
                    <button
                      key={index}
                      onClick={() => handleOptionSelect(index)}
                      className={`w-full text-left p-4 rounded-xl border-2 transition-all ${
                        currentAnswer?.selectedOption === index
                          ? 'border-primary-500 bg-primary-50 text-primary-900'
                          : 'border-secondary-200 hover:border-secondary-300 hover:bg-secondary-50'
                      }`}
                    >
                      <div className="flex items-center space-x-3">
                        <div className={`w-6 h-6 rounded-full border-2 flex items-center justify-center ${
                          currentAnswer?.selectedOption === index
                            ? 'border-primary-500 bg-primary-500'
                            : 'border-secondary-300'
                        }`}>
                          {currentAnswer?.selectedOption === index && (
                            <div className="w-2 h-2 bg-white rounded-full"></div>
                          )}
                        </div>
                        <span className="font-medium text-sm">
                          {String.fromCharCode(65 + index)}.
                        </span>
                        <span>{option}</span>
                      </div>
                    </button>
                  ))}
                </div>

                {/* Navigation */}
                <div className="flex items-center justify-between mt-8 pt-6 border-t border-secondary-200">
                  <button
                    onClick={() => navigateToQuestion(currentQuestionIndex - 1)}
                    disabled={currentQuestionIndex === 0}
                    className="flex items-center px-4 py-2 text-sm font-medium text-secondary-700 bg-white border border-secondary-300 rounded-lg hover:bg-secondary-50 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    <ChevronLeft className="h-4 w-4 mr-2" />
                    <span>Previous</span>
                  </button>

                  <div className="flex items-center space-x-2">
                    <span className="text-sm text-secondary-600">
                      {getAnsweredCount()} of {questions.length} answered
                    </span>
                  </div>

                  <button
                    onClick={() => navigateToQuestion(currentQuestionIndex + 1)}
                    disabled={currentQuestionIndex === questions.length - 1}
                    className="flex items-center px-4 py-2 text-sm font-medium text-secondary-700 bg-white border border-secondary-300 rounded-lg hover:bg-secondary-50 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    <span>Next</span>
                    <ChevronRight className="h-4 w-4 ml-2" />
                  </button>
                </div>
              </div>
            </div>
          </div>

          {/* Question Navigator */}
          <div className="lg:col-span-1">
            <div className="card sticky top-24">
              <div className="card-header">
                <h3 className="text-lg font-semibold text-secondary-900">Question Navigator</h3>
                <p className="text-sm text-secondary-600">{questions.length} Questions</p>
              </div>
              <div className="card-content">
                {/* Subject-wise Question Grid */}
                <div className="space-y-4">
                  {['mathematics', 'physics', 'chemistry'].map((subject) => {
                    const subjectQuestions = questions.filter(q => q.subject === subject);
                    if (subjectQuestions.length === 0) return null;
                    
                    return (
                      <div key={subject} className="space-y-2">
                        <h4 className={`text-sm font-semibold capitalize ${
                          subject === 'mathematics' ? 'text-primary-700' :
                          subject === 'physics' ? 'text-success-700' :
                          'text-warning-700'
                        }`}>
                          {subject} ({subjectQuestions.length} questions)
                        </h4>
                        <div className="grid grid-cols-5 gap-1">
                          {subjectQuestions.map((question) => {
                            const globalIndex = questions.findIndex(q => q.id === question.id);
                            return (
                              <button
                                key={question.id}
                                onClick={() => navigateToQuestion(globalIndex)}
                                className={`w-8 h-8 rounded-lg text-xs font-medium transition-all ${
                                  globalIndex === currentQuestionIndex
                                    ? 'bg-primary-500 text-white'
                                    : getQuestionStatus(question.id) === 'answered'
                                    ? 'bg-success-100 text-success-700 hover:bg-success-200'
                                    : 'bg-secondary-100 text-secondary-600 hover:bg-secondary-200'
                                }`}
                              >
                                {globalIndex + 1}
                              </button>
                            );
                          })}
                        </div>
                      </div>
                    );
                  })}
                </div>

                <div className="mt-6 space-y-2 text-sm">
                  <div className="flex items-center space-x-2">
                    <div className="w-4 h-4 bg-success-100 rounded border border-success-300"></div>
                    <span className="text-secondary-600">Answered ({getAnsweredCount()})</span>
                  </div>
                  <div className="flex items-center space-x-2">
                    <div className="w-4 h-4 bg-secondary-100 rounded border border-secondary-300"></div>
                    <span className="text-secondary-600">Not Answered ({questions.length - getAnsweredCount()})</span>
                  </div>
                  <div className="flex items-center space-x-2">
                    <div className="w-4 h-4 bg-primary-500 rounded"></div>
                    <span className="text-secondary-600">Current Question</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Submit Confirmation Modal */}
      {showSubmitConfirm && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-2xl p-6 max-w-md w-full mx-4">
            <h3 className="text-xl font-semibold text-secondary-900 mb-4">Submit Test?</h3>
            <p className="text-secondary-600 mb-6">
              Are you sure you want to submit the test? You have answered {getAnsweredCount()} out of {questions.length} questions.
              You won't be able to change your answers after submission.
            </p>
            <div className="flex space-x-3">
              <button
                onClick={() => setShowSubmitConfirm(false)}
                className="flex-1 px-4 py-2 text-sm font-medium text-secondary-700 bg-white border border-secondary-300 rounded-lg hover:bg-secondary-50 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                disabled={submitting}
              >
                Cancel
              </button>
              <button
                onClick={handleSubmitTest}
                className="flex-1 px-4 py-2 text-sm font-medium text-white bg-primary-600 border border-primary-600 rounded-lg hover:bg-primary-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                disabled={submitting}
              >
                {submitting ? 'Submitting...' : 'Submit Test'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}